import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/DashboardBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

export default function StaffPage() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const load = () => api.get("/staff").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        title={t("staff")}
        subtitle={`${list.length} active`}
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-950 hover:bg-slate-800" data-testid="add-staff-button"><Plus className="h-4 w-4 mr-1" /> {t("add_staff")}</Button>
            </DialogTrigger>
            <StaffFormDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Designation</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No office staff yet.</TableCell></TableRow>)}
              {list.map((s) => (
                <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{s.email}</TableCell>
                  <TableCell className="text-slate-600">{s.phone}</TableCell>
                  <TableCell className="text-slate-600">{s.designation || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenUser(s)} data-testid={`staff-create-user-${s.id}`}><KeyRound className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={async () => {
                      if (!window.confirm("Delete staff?")) return;
                      try { await api.delete(`/staff/${s.id}`); toast.success("Deleted"); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid={`staff-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {openUser && <CreateUserDialog entity={openUser} role="office" onClose={() => setOpenUser(null)} />}
    </div>
  );
}

function StaffFormDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", designation: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/staff", form); toast.success("Staff added"); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle className="font-heading">Add Office Staff</DialogTitle><DialogDescription>Register an internal office team member.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
        <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone" /></div>
        <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="staff-designation" /></div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="staff-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function CreateUserDialog({ entity, role, onClose }) {
  const [form, setForm] = useState({ email: entity?.email || "", password: "", name: entity?.name || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/register", {
        email: form.email.toLowerCase(), password: form.password, name: form.name,
        role, entity_id: entity.id, phone: entity.phone,
      });
      toast.success("Login user created");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Create Staff Login</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Temporary Password</Label><Input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
